import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  ListsToggle,
  CreateLink,
  InsertThematicBreak,
  InsertImage,
  insertImage$,
  saveImage$,
  closeImageDialog$,
  imageDialogState$,
  useCellValue,
  usePublisher,
} from '@mdxeditor/editor';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useMemo,
  useRef,
} from 'react';

import '@mdxeditor/editor/style.css';

import { useTownAssets } from '../../stores';
import { imageSrcFromId } from '../../utils';

import CompactImagePicker from './CompactImagePicker';

function PxImageDialog() {
  const state = useCellValue(imageDialogState$);
  const insertImage = usePublisher(insertImage$);
  const saveImage = usePublisher(saveImage$);
  const closeImageDialog = usePublisher(closeImageDialog$);
  const { assets } = useTownAssets();
  const { translate } = assets;

  if (state.type === 'inactive') return null;

  const isEditing = state.type === 'editing';
  const editSrc = isEditing ? (state.initialValues?.src ?? '') : '';
  const editImageId = editSrc.startsWith('pximg:') ? editSrc.slice(6) : undefined;

  return (
    <CompactImagePicker
      id="mdImage"
      initialValue={editImageId}
      initialAlt={isEditing ? (state.initialValues?.altText ?? '') : ''}
      altInput
      altInputLabel={translate('description')}
      pickingDone={({ value: imageId, altText }) => {
        if (isEditing) {
          saveImage({
            src: imageId ? `pximg:${imageId}` : state.initialValues?.src,
            altText: altText ?? '',
            title: state.initialValues?.title ?? '',
          });
        } else if (imageId) {
          insertImage({ src: `pximg:${imageId}`, altText: altText ?? '' });
        }
        closeImageDialog();
      }}
      onClose={() => closeImageDialog()}
      types={['comm', 'generic']}
      uploadType="comm"
    />
  );
}

function ToolbarContents() {
  return (
    <>
      <BoldItalicUnderlineToggles options={['Bold', 'Italic']} />
      <BlockTypeSelect />
      <ListsToggle options={['bullet', 'number']} />
      <CreateLink />
      <InsertThematicBreak />
    </>
  );
}

function ToolbarContentsWithImages() {
  return (
    <>
      <BoldItalicUnderlineToggles options={['Bold', 'Italic']} />
      <BlockTypeSelect />
      <ListsToggle options={['bullet', 'number']} />
      <CreateLink />
      <InsertThematicBreak />
      <InsertImage />
    </>
  );
}

function MarkdownEditor(props) {
  const {
    value,
    onChange,
    placeholder,
    className,
    invalid,
    withImages,
    piid,
  } = props;
  const editorRef = useRef(null);
  const { assets } = useTownAssets();
  const { getSecuredImg } = assets;

  const handleChange = useCallback(
    (newValue) => { onChange(newValue.replace(/(\n\s*){3,}/g, '\n\n\u00a0\n\n')); },
    [onChange],
  );

  const imagePreviewHandler = useCallback(
    async (src) => {
      if (src.startsWith('pximg:')) {
        const imageId = src.slice(6);
        const urlPath = imageSrcFromId(imageId);
        const blobUrl = await getSecuredImg(urlPath, piid);
        return blobUrl ?? src;
      }
      return src;
    },
    [getSecuredImg, piid],
  );

  const plugins = useMemo(
    () => {
      const list = [
        headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4] }),
        listsPlugin(),
        linkPlugin(),
        linkDialogPlugin(),
        thematicBreakPlugin(),
      ];

      if (withImages) {
        list.push(imagePlugin({
          imagePreviewHandler,
          disableImageResize: true,
          disableImageSettingsButton: false,
          ImageDialog: PxImageDialog,
        }));
      }

      list.push(toolbarPlugin({
        toolbarContents: withImages ? ToolbarContentsWithImages : ToolbarContents,
      }));

      return list;
    },
    [withImages, imagePreviewHandler],
  );

  return (
    <div
      className={classNames(
        'border rounded-lg px-md-editor',
        invalid ? 'border-pxUiAlert' : 'border-pxOutlineStrong',
        className,
      )}
    >
      <MDXEditor
        ref={editorRef}
        markdown={value}
        onChange={handleChange}
        placeholder={placeholder}
        plugins={plugins}
      />
    </div>
  );
}

MarkdownEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  invalid: PropTypes.bool,
  withImages: PropTypes.bool,
  piid: PropTypes.string,
};

export default MarkdownEditor;
